import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import {
    Button,
} from '@connected/ui/components/button';
import {
    Input,
} from '@connected/ui/components/input';
import {
    Popout,
    PopoutBody,
    PopoutContent,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
    PopoutTrigger,
} from '@connected/ui/components/popout';
import { Separator } from '@connected/ui/components/separator';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Page, Section } from '@connected/ui/features/layouts';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { ChevronRight, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Loading } from '@/components/loading';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useInverters } from '@/hooks/queries/use-inverters';
import { useFormTouched } from '@/hooks/use-form-touched';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/pv-inverters/',
)({
    component: PVInvertersRoutePage,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
    pendingComponent: () => <Loading action="Omvormers aan het laden" />,
});

function PVInvertersRoutePage() {
    const { t } = useTranslation('re-inspections');

    return (
        <Page>
            <Section>
                <Section.Header>
                    <Section.Title>
                        {t(
                            'installation.layout.navigation.pv-inverters.label',
                        )}
                    </Section.Title>
                </Section.Header>
                <Section.Content>
                    <Form />
                </Section.Content>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Section>
        </Page>
    );
}

type PVInverterFormValues = {
    name: string;
};

type FormValues = {
    pvSolarIrradiance: number | null;
    pvSolarPanelTemperature: number | null;
};

function Form() {
    const params = Route.useParams();

    const [searchField, setSearchField] = useState<string>('');

    const { t } = useTranslation('re-inspections');

    const inverters = useInverters({
        electricalProjectVersionId: params.projectVersionId,
    });

    const { data } = useQuery(elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
        electricalProjectVersionId: params.projectVersionId,
    }));

    /// api/v2/electrical/projects/inspections/edit

    const form = useForm<FormValues, ZodValidator>({
        defaultValues: {
            pvSolarIrradiance: data?.Inspection?.PVSolarIrradiance ?? null,
            pvSolarPanelTemperature: data?.Inspection?.PVSolarPanelTemperature ?? null,
        },
        validatorAdapter: zodValidator(),
    });

    const { patchField } = usePatcher('/api/v2/electrical/projects/inspections/patch', {
        scope: {
            id: params.projectVersionId,
        },
        onMutate: async (variable) => {
            const body = toPascalCase(variable?.body)!;

            const queryKey = elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
                electricalProjectVersionId: params.projectVersionId,
            }).queryKey;

            const previousData = queryClient.getQueryData<components['schemas']['EditElectricalProjectInspectionQueryResult']>(queryKey);

            const newData = {
                ...previousData,
                Inspection: {
                    ...previousData?.Inspection,
                    ...body,
                },
            };

            queryClient.setQueryData<components['schemas']['EditElectricalProjectInspectionQueryResult']>(
                queryKey,
                newData,
            );
            return { previousData };
        },
    }, {
        body: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    return (
        <div className="flex flex-col items-center gap-4 md:grid-cols-2">
            <div className="flex w-full flex-col gap-4">

                <form.Field
                    name="pvSolarIrradiance"
                    validators={{
                        onChange: validators.patchElectricalProjectInspectionCommand.pVSolarIrradiance,
                        onChangeAsync: patchField('PVSolarIrradiance'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('installation.pv-inverters.form.solar-irradiance.label')}
                        >
                            <NumberField
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('installation.pv-inverters.form.solar-irradiance.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
                <form.Field
                    name="pvSolarPanelTemperature"
                    validators={{
                        onChange: validators.patchElectricalProjectInspectionCommand.pVSolarPanelTemperature,
                        onChangeAsync: patchField('PVSolarPanelTemperature'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('installation.pv-inverters.form.solar-panel-temperature.label')}
                        >
                            <NumberField
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('installation.pv-inverters.form.solar-panel-temperature.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
            </div>
            <Separator className="w-full" />
            <div className="w-full grid grid-cols-5 gap-4">
                <Input
                    type="search"
                    className="w-full col-span-4"
                    placeholder={t('actions.search.search', { ns: 'common' })}
                    leftSlot={<Search size={20} />}
                    onChange={e => setSearchField(e.target.value)}
                />
                <CreateInverterModal />
            </div>
            {inverters.isLoading
                ? (
                        <div className="flex w-full flex-col gap-4">
                            <Skeleton className="w-full h-10" />
                            <Skeleton className="w-full h-10" />
                            <Skeleton className="w-full h-10" />
                            <Skeleton className="w-full h-10" />
                        </div>
                    )
                : (
                        <>
                            {
                                inverters.data?.inverters?.filter(
                                    x => x.Identifier?.toLowerCase().includes(searchField.toLowerCase()),
                                )?.map(inverter => (
                                    <PVInverter
                                        key={inverter.ElectricalProjectPVInverterId}
                                        electricalProjectVersionId={params.projectVersionId}
                                        inverter={inverter}
                                    />
                                ))
                            }
                            {inverters?.data?.count === 0 && (
                                <em>Geen omvormers</em>
                            )}
                        </>

                    )}
        </div>
    );
}

function PVInverter({
    electricalProjectVersionId,
    inverter,
}: {
    electricalProjectVersionId: string;
    inverter: components['schemas']['ListElectricalProjectPVInverterQueryResultInverter'];
}) {
    const params = Route.useParams();
    const search = Route.useSearch();
    const { t: c } = useTranslation('common');

    const deleteInverterMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/pvinverters/delete',
        {
            onMutate: async (variable) => {
                const inverterQueryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId });
                const previousDataList = queryClient.getQueryData(inverterQueryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'];
                const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId,
                });

                await queryClient.cancelQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId }));
                await queryClient.cancelQueries(summaryOptions);

                queryClient.setQueryData<components['schemas']['ListElectricalProjectPVInverterQueryResult']>(
                    elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId }).queryKey,
                    (old) => {
                        if (old) {
                            const updatedData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                                ...old,
                                Inverters: old.Inverters.filter(
                                    x => x.ElectricalProjectPVInverterId !== variable.params.query.ElectricalProjectPVInverterId,
                                ),
                            };
                            return updatedData;
                        }
                        return old;
                    },
                );

                queryClient.setQueryData<components['schemas']['ViewElectricalProjectSummaryQueryResult']>(
                    summaryOptions.queryKey,
                    (old) => {
                        if (old && old.Summary) {
                            return {
                                ...old,
                                Summary: {
                                    ...old.Summary,
                                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                                    // DistributionBoardCount: (old.Summary.DistributionBoardCount || 1) - 1, FIX: Decrement omvormer count
                                },
                            };
                        }
                        return old;
                    },
                );

                return { previousDataList };
            },
            onSuccess: () => {
                toast.success(c('messages.toast.inverterDeleted'));
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId }),
                );
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    async function handleDelete({
        inverterId,
    }: {
        inverterId: string;
    }) {
        if (await showConfirm(c('messages.confirmDelete'))) {
            deleteInverterMutation.mutate({
                params: {
                    query: {
                        ElectricalProjectPVInverterId: inverterId,
                    },
                },
            });
        }
    }

    return (
        <div className="flex w-full flex-row items-center justify-between">
            <div className="flex items-center gap-4">
                <Button
                    variant="outline"
                    className="flex justify-between gap-2 bg-background-tertiary text-text-primary"
                    asChild
                >
                    <Link
                        to="/projects/$projectVersionId/re-inspections/pv-inverters/$inverterId"
                        params={{
                            ...params,
                            inverterId: inverter.ElectricalProjectPVInverterId!,
                        }}
                        search={search}
                    >
                        {inverter.Identifier
                            || 'Naamloze omvormer'}
                        <ChevronRight />
                    </Link>
                </Button>
            </div>
            <Button
                variant="ghost"
                onClick={() => {
                    handleDelete({
                        inverterId: inverter.ElectricalProjectPVInverterId!,
                    });
                }}
                disabled={deleteInverterMutation.isPending}
            >
                <Trash2 />
            </Button>
        </div>
    );
}

function CreateInverterModal() {
    const params = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const [newInverterId, setNewInverterId] = useState<string>(() => crypto.randomUUID());
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        // Reset the new distribution board ID when the modal is opened
        // This ensures that a new ID is generated each time the modal is opened
        const newId = crypto.randomUUID();
        logger.debug(['Resetting new inverter  ID', newId]);

        // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
        setNewInverterId(newId);
    }, [isOpen]);

    const createInverterMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/pvinverters/create',
        {
            onMutate: async (variable) => {
                const pvInverterQueryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                    electricalProjectVersionId: params.projectVersionId,
                });

                const previousDataList = queryClient.getQueryData(pvInverterQueryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'];

                const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId: params.projectVersionId,
                });

                await queryClient.cancelQueries(pvInverterQueryOptions);
                await queryClient.cancelQueries(summaryOptions);

                queryClient.setQueryData<components['schemas']['ListElectricalProjectPVInverterQueryResult']>(pvInverterQueryOptions.queryKey, (old) => {
                    if (old && variable) {
                        const updatedData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                            ...old,
                            Inverters: [
                                ...(old.Inverters || []),
                                {
                                    ElectricalProjectPVInverterId: variable?.body?.ElectricalProjectPVInverterId,
                                    Identifier: variable?.body?.Identifier,
                                    Order: 9999,
                                },
                            ],
                        };
                        return updatedData;
                    }
                    return old;
                });

                queryClient.setQueryData<components['schemas']['ViewElectricalProjectSummaryQueryResult']>(
                    summaryOptions.queryKey,
                    (old) => {
                        if (old && old.Summary) {
                            return {
                                ...old,
                                Summary: {
                                    ...old.Summary,
                                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                                    // DistributionBoardCount: (old.Summary.DistributionBoardCount || 0) + 1, // FIX: Increment omvormer count
                                },
                            };
                        }
                        return old;
                    },
                );

                navigate({
                    to: '/projects/$projectVersionId/re-inspections/pv-inverters/$inverterId',
                    params: {
                        ...params,
                        inverterId: variable!.body!.ElectricalProjectPVInverterId!,
                    },
                    search,
                });

                return { previousDataList };
            },
            onSuccess: async () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId }),
                );
            },
            onError: (error, _variable, context: any) => {
                console.error(error);

                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId }));

                if (context?.previousDataList) {
                    queryClient.setQueryData(
                        elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId }).queryKey,
                        context.previousDataList,
                    );
                }
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const defaultValues: PVInverterFormValues = {
        name: '',
    };

    const { Field, handleSubmit, reset, store } = useForm<
        PVInverterFormValues,
        ZodValidator
    >({
        defaultValues,
        onSubmit: ({ value }) => {
            createInverterMutation.mutate({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    Identifier: value.name,
                    ElectricalProjectPVInverterId: newInverterId,
                },
            });
        },
        validatorAdapter: zodValidator(),
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm(c('messages.confirmClose'))) {
                setIsOpen(false);
                reset();
                createInverterMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
            createInverterMutation.reset();
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutTrigger asChild>
                <Button variant="outline" outline="primary">{c('actions.general.add')}</Button>
            </PopoutTrigger>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {t('installation.pv-inverters.modal.addTitle')}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form
                        onSubmit={onSubmit}
                        className="flex flex-col gap-4 py-6"
                    >
                        <Field
                            name="name"
                            validators={{
                                onChange:
                                    validators.createElectricalProjectPVInverterCommand.identifier,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    label={t('installation.pv-inverters.form.name.label')}
                                >
                                    <Input
                                        type="text"
                                        name={name}
                                        onChange={e =>
                                            handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('installation.pv-inverters.form.name.placeholder')}
                                    />
                                </Label>
                            )}
                        </Field>
                    </form>
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        variant="outline"
                        disabled={createInverterMutation.isPending}
                    >
                        {c('actions.general.cancel_short')}
                    </Button>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={createInverterMutation.isPending}
                    >
                        {c('actions.general.save')}
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>
    );
}
